import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import PortfolioSummaryCard from './components/PortfolioSummaryCard';
import PerformanceChart from './components/PerformanceChart';
import StockCard from './components/StockCard';
import QuickActionsPanel from './components/QuickActionsPanel';
import MarketNewsCard from './components/MarketNewsCard';
import MarketIndicesWidget from './components/MarketIndicesWidget';
import RecentTransactions from './components/RecentTransactions';

const Dashboard = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const navigate = useNavigate();

  // Mock portfolio data
  const portfolioSummary = [
    {
      title: 'Total Portfolio Value',
      value: '₹10,434,430.50',
      change: 194340.25,
      changePercent: 1.90,
      icon: 'DollarSign',
      trend: 'up'
    },
    {
      title: 'Today\'s Change',
      value: '₹194,340.25',
      change: 194340.25,
      changePercent: 1.90,
      icon: 'TrendingUp',
      trend: 'up'
    },
    {
      title: 'Total Gain/Loss',
      value: '₹1,284,340.50',
      change: 1284340.50,
      changePercent: 14.05,
      icon: 'Target',
      trend: 'up'
    }
  ];

  // Mock top performers data
  const topPerformers = [
    {
      symbol: 'AAPL',
      name: 'Apple Inc.',
      price: '14,610.50',
      change: 436.25,
      changePercent: 3.08,
      volume: '2.5M',
      isWatchlisted: true
    },
    {
      symbol: 'GOOGL',
      name: 'Alphabet Inc.',
      price: '237,234.25',
      change: 3810.80,
      changePercent: 1.63,
      volume: '1.2M',
      isWatchlisted: false
    },
    {
      symbol: 'MSFT',
      name: 'Microsoft Corp.',
      price: '34,610.75',
      change: 740.90,
      changePercent: 2.19,
      volume: '3.1M',
      isWatchlisted: true
    }
  ];

  // Mock biggest losers data
  const biggestLosers = [
    {
      symbol: 'TSLA',
      name: 'Tesla Inc.',
      price: '20,440.80',
      change: -1036.45,
      changePercent: -4.82,
      volume: '4.2M',
      isWatchlisted: false
    },
    {
      symbol: 'NFLX',
      name: 'Netflix Inc.',
      price: '40,380.30',
      change: -724.70,
      changePercent: -1.76,
      volume: '1.8M',
      isWatchlisted: true
    }
  ];

  // Mock market news data
  const marketNews = [
    {
      title: 'Federal Reserve Signals Potential Rate Cut in Q4 2025',
      summary: 'Fed officials hint at monetary policy adjustments as inflation shows signs of cooling across major economic indicators.',
      source: 'Financial Times',
      publishedAt: '2025-07-11T06:30:00Z',
      imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=300&fit=crop',
      category: 'market'
    },
    {
      title: 'Tech Earnings Season Kicks Off with Strong Expectations',
      summary: 'Major technology companies prepare to report Q2 earnings with analysts forecasting robust growth in cloud and AI sectors.',
      source: 'Bloomberg',
      publishedAt: '2025-07-11T05:15:00Z',
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
      category: 'earnings'
    },
    {
      title: 'Energy Sector Rallies on Supply Chain Improvements',
      summary: 'Oil and gas companies see significant gains as global supply chain disruptions ease and demand stabilizes.',
      source: 'Reuters',
      publishedAt: '2025-07-11T04:45:00Z',
      imageUrl: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop',
      category: 'analysis'
    }
  ];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLastUpdated(new Date());
    setIsRefreshing(false);
  };

  const handleNavigateToPortfolio = () => {
    navigate('/portfolio-management');
  };

  const handleNavigateToSearch = () => {
    navigate('/stock-search-discovery');
  };

  useEffect(() => {
    // Simulate real-time updates
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar />
        
        <main className="flex-1 p-4 lg:p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                Welcome back! Here's your portfolio overview for today.
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-sm text-muted-foreground">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                loading={isRefreshing}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Portfolio Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioSummary.map((item, index) => (
              <PortfolioSummaryCard
                key={index}
                title={item.title}
                value={item.value}
                change={item.change}
                changePercent={item.changePercent}
                icon={item.icon}
                trend={item.trend}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Charts and Performance */}
            <div className="lg:col-span-2 space-y-6">
              {/* Performance Chart */}
              <PerformanceChart />

              {/* Stock Performance Sections */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Top Performers */}
                <div className="card-elevated p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-success/10 rounded-lg">
                        <Icon name="TrendingUp" size={20} className="text-success" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">Top Performers</h3>
                        <p className="text-sm text-muted-foreground">Best performing stocks today</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleNavigateToSearch}>
                      <span className="text-sm">View All</span>
                      <Icon name="ChevronRight" size={16} className="ml-1" />
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    {topPerformers.map((stock) => (
                      <StockCard
                        key={stock.symbol}
                        symbol={stock.symbol}
                        name={stock.name}
                        price={stock.price}
                        change={stock.change}
                        changePercent={stock.changePercent}
                        volume={stock.volume}
                        isWatchlisted={stock.isWatchlisted}
                      />
                    ))}
                  </div>
                </div>

                {/* Biggest Losers */}
                <div className="card-elevated p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-destructive/10 rounded-lg">
                        <Icon name="TrendingDown" size={20} className="text-destructive" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">Biggest Losers</h3>
                        <p className="text-sm text-muted-foreground">Declining stocks today</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleNavigateToSearch}>
                      <span className="text-sm">View All</span>
                      <Icon name="ChevronRight" size={16} className="ml-1" />
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    {biggestLosers.map((stock) => (
                      <StockCard
                        key={stock.symbol}
                        symbol={stock.symbol}
                        name={stock.name}
                        price={stock.price}
                        change={stock.change}
                        changePercent={stock.changePercent}
                        volume={stock.volume}
                        isWatchlisted={stock.isWatchlisted}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Market News */}
              <div className="card-elevated p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-secondary/10 rounded-lg">
                      <Icon name="Newspaper" size={20} className="text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Market News</h3>
                      <p className="text-sm text-muted-foreground">Latest financial headlines</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <span className="text-sm">View All</span>
                    <Icon name="ExternalLink" size={16} className="ml-1" />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {marketNews.map((news, index) => (
                    <MarketNewsCard
                      key={index}
                      title={news.title}
                      summary={news.summary}
                      source={news.source}
                      publishedAt={news.publishedAt}
                      imageUrl={news.imageUrl}
                      category={news.category}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Widgets and Actions */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <QuickActionsPanel />

              {/* Market Indices */}
              <MarketIndicesWidget />

              {/* Recent Transactions */}
              <RecentTransactions />

              {/* Portfolio Allocation Preview */}
              <div className="card-elevated p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon name="PieChart" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Portfolio Allocation</h3>
                      <p className="text-sm text-muted-foreground">Asset distribution</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleNavigateToPortfolio}>
                    <span className="text-sm">View Details</span>
                    <Icon name="ChevronRight" size={16} className="ml-1" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-primary rounded-full"></div>
                      <span className="text-sm text-foreground">Technology</span>
                    </div>
                    <span className="text-sm font-mono text-foreground">45%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-success rounded-full"></div>
                      <span className="text-sm text-foreground">Healthcare</span>
                    </div>
                    <span className="text-sm font-mono text-foreground">25%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-secondary rounded-full"></div>
                      <span className="text-sm text-foreground">Finance</span>
                    </div>
                    <span className="text-sm font-mono text-foreground">20%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-accent rounded-full"></div>
                      <span className="text-sm text-foreground">Energy</span>
                    </div>
                    <span className="text-sm font-mono text-foreground">10%</span>
                  </div>
                </div>

                <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full flex">
                    <div className="bg-primary" style={{ width: '45%' }}></div>
                    <div className="bg-success" style={{ width: '25%' }}></div>
                    <div className="bg-secondary" style={{ width: '20%' }}></div>
                    <div className="bg-accent" style={{ width: '10%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;